import { Injectable } from '@angular/core';

import { UrlService } from '@service/url/url.service';
import {
	RestService,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NOT_EMPTY,
	IS_NULL_OR_UNDEFINED,
} from 'cargo-chief-commons';
import { Cargochief } from '@service/cargochief/cargochief.service';

import { CARGOCHIEF_KEYS } from '@domain/constants/app-variables';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';

@Injectable({
	providedIn: 'root',
})
export class BaConfigService {
	constructor(
		private urlService: UrlService,
		private restService: RestService,
		public cargochief: Cargochief,
		private utilService: UtilService
	) {}

	async setBaConfig() {
		const response = await this.restService
			.GET(this.urlService.getBookingAutomationConfigurationData())
			.toPromise();
		if (response?.data) {
			this.cargochief.setItem({
				[CARGOCHIEF_KEYS.BA_CONFIG]: response.data,
			});
		}
	}

	get IS_BA_ENABLED(): boolean {
		let result: boolean = false;
		const baConfig = this.getBAConfig();
		if (baConfig?.bookingAssistantEnabledValue) {
			result = Boolean(JSON.parse(baConfig.bookingAssistantEnabledValue));
		}
		return result;
	}

	get IS_BOOK_NOW_ENABLED(): boolean {
		let result: boolean = false;
		const baConfig = this.getBAConfig();
		try {
			let baConfigData = JSON.parse(baConfig.data);
			if (baConfigData?.isBookNowOn) {
				result = Boolean(JSON.parse(baConfigData.isBookNowOn));
			}
		} catch (error) {
			console.warn('IS_BOOK_NOW_ENABLED failed: ', error);
		}
		return result;
	}

	/**
	 * CargoChief-set per-account entitlement that gates ACCESS to the Automated Carrier Negotiation
	 * feature — i.e. whether the account can even SEE it in the app (BA Config section + the per-workflow
	 * override). It is not the on/off switch (that is autoAcceptMode). Default false: an account sees the
	 * feature only when CargoChief has explicitly entitled it.
	 */
	get AUTO_ACCEPT_ENTITLED(): boolean {
		let result: boolean = false;
		const baConfig = this.getBAConfig();
		try {
			const baConfigData = JSON.parse(baConfig.data);
			result = Boolean(baConfigData?.autoAcceptEntitled);
		} catch (error) {
			console.warn('AUTO_ACCEPT_ENTITLED failed: ', error);
		}
		return result;
	}

	/**
	 * Account-level Automated Carrier Negotiation defaults, used to seed a per-workflow
	 * override when it is first turned on (resolution is workflow ?? account, so overriding
	 * starts from the account values). Dollar margins are stored in cents; `marginDisplay`
	 * is already converted to dollars to match the workflow form's display value.
	 */
	getAccountAutoAcceptDefaults(): {
		enabled: boolean;
		marginType: string;
		marginDisplay: number;
		timeXHours: number;
		bookEnabled: boolean;
	} {
		const defaults = {
			enabled: false,
			marginType: 'Percentage',
			marginDisplay: null,
			timeXHours: null,
			bookEnabled: false,
		};
		const baConfig = this.getBAConfig();
		try {
			const data = JSON.parse(baConfig.data);
			const mode = data?.autoAcceptMode;
			defaults.enabled = mode
				? String(mode).toUpperCase() === 'LIVE'
				: Boolean(data?.autoAcceptEnabled);
			defaults.bookEnabled = Boolean(data?.autoAcceptBookEnabled);
			if (data?.autoAcceptMarginType) {
				defaults.marginType = data.autoAcceptMarginType;
			}
			if (IS_NOT_NULL_OR_UNDEFINED(data?.autoAcceptMarginValue)) {
				defaults.marginDisplay =
					defaults.marginType === 'Dollar'
						? data.autoAcceptMarginValue / 100
						: data.autoAcceptMarginValue;
			}
			if (IS_NOT_NULL_OR_UNDEFINED(data?.autoAcceptTimeXHours)) {
				defaults.timeXHours = data.autoAcceptTimeXHours;
			}
		} catch (error) {
			console.warn('getAccountAutoAcceptDefaults failed: ', error);
		}
		return defaults;
	}

	getBAConfig(): any {
		const baConfig: any = localStorage.getItem(CARGOCHIEF_KEYS.BA_CONFIG);
		if (IS_NOT_NULL_OR_UNDEFINED(baConfig) && IS_NOT_EMPTY(baConfig)) {
			return JSON.parse(baConfig);
		}
		return null;
	}

	get OUT_OF_NETWORK_ENABLED(): any {
		let result = false;
		const baConfig: any = this.getBAConfig();
		if (baConfig?.data) {
			const data = JSON.parse(baConfig.data);
			result = data.enableOutOfNetworkCarriers && IS_NOT_NULL_OR_UNDEFINED(data?.outOfNetworkDelay);
		}
		return result;
	}

	get IS_OUT_OF_NETWORK_ALLOWED(): boolean {
		let result = false;
		const baConfig: any = this.getBAConfig();
		if (baConfig?.data) {
			const data = JSON.parse(baConfig.data);
			result = Boolean(data.enableOutOfNetworkCarriers);
		}
		return result;
	}

	isCarrierAddedToBookNowConfig(carrierId: string): boolean {
		let result = false;
		let baConfig = this.getBAConfig();
		try {
			baConfig = JSON.parse(baConfig.data);
			let bookNowData = baConfig['bookNowData'] || [];
			const bookNowCarriersList =
				bookNowData?.find((config) => config.attribute == 'carrier')?.data || [];
			const carrierFound = bookNowCarriersList?.find((c) => c.id == carrierId);
			result = IS_NOT_NULL_OR_UNDEFINED(carrierFound);
		} catch (error) {
			console.warn('isCarrierAddedToBookNowConfig failed: ', error);
		}

		return result;
	}

	async updateBookNowData(carrier: { id: string; businessName: string }, addCarrier: boolean) {
		let baConfig = this.getBAConfig();
		try {
			let baConfigData = JSON.parse(baConfig.data);
			if (!baConfigData?.bookNowData) {
				baConfigData['bookNowData'] = [];
			}
			let bookNowData = baConfigData.bookNowData;

			if (addCarrier) {
				let bookNowCarrierData = bookNowData?.find((config) => config.attribute == 'carrier');
				if (!bookNowCarrierData) {
					bookNowCarrierData = {
						attribute: 'carrier',
						operator: 'in',
						data: [carrier],
					};
					bookNowData.push(bookNowCarrierData);
				} else {
					bookNowData.forEach((config) => {
						if (config.attribute == 'carrier') {
							config.data.push(carrier);
						}
					});
				}
			} else {
				bookNowData.forEach((config) => {
					if (config.attribute == 'carrier') {
						config.data = config.data.filter((c) => c.id !== carrier.id);
					}
				});
			}
			bookNowData.forEach((config) => {
				config.data = config.data.filter(
					(value, index, self) => index === self.findIndex((c) => c.id === value.id)
				);
			});
			baConfigData['bookNowData'] = bookNowData;
			baConfig['data'] = baConfigData;
			const successMessage = `${carrier.businessName} ${addCarrier ? 'added to' : 'removed from'} Book Now.`;
			await this.updateBaConfig(baConfig, successMessage);
		} catch (error) {
			console.warn('updateBookNowData failed: ', error);
			this.utilService.showSnackbar(
				'Failed to update Book Now configuration.',
				SNACKBAR.ERROR,
				SNACKBAR.ACTION
			);
		}
	}

	async updateBaConfig(baConfig: any, successMessage: string) {
		try {
			delete baConfig.created;
			delete baConfig.updated;
			delete baConfig.accountId;
			if (IS_NULL_OR_UNDEFINED(baConfig.isBookingAssistantEnabledChecked)) {
				baConfig['isBookingAssistantEnabledChecked'] =
					baConfig.bookingAssistantEnabledValue == 'true' ||
					baConfig.bookingAssistantEnabledValue == true;
				delete baConfig.bookingAssistantEnabledValue;
			}
			const response = await this.restService
				.POST(this.urlService.updateBookingAutomationConfigurationData(), baConfig)
				.toPromise();
			this.utilService.showSnackbar(successMessage, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
			this.cargochief.setItem({
				[CARGOCHIEF_KEYS.BA_CONFIG]: response.data,
			});
		} catch (error) {
			this.utilService.showSnackbar('Failed to update config.', SNACKBAR.ERROR, SNACKBAR.ACTION);
		}
	}
}
